<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";

interface Language {
  id: string;
  translationCode: {
    code: string;
    name?: string;
  };
  name?: string;
}

const props = defineProps<{
  type?: string;
}>();

const router = useRouter();
const route = useRoute();
const { locale } = useI18n();

const { languages, changeLanguage, replaceToDevStorefront } =
  useInternationalization();

const { languageIdChain } = useSessionContext();

// Computed property for current language
const currentLanguage = computed(() => {
  return Array.isArray(languages.value)
    ? languages.value.find((lang) => lang.id === languageIdChain.value)
    : null;
});

// Format language display
const formatLanguageDisplay = (language: Language) => {
  return language.translationCode.code.substring(0, 2).toUpperCase();
};

// Dropdown state
const isDropdownOpen = ref(false);
const dropdownRef = ref<HTMLDivElement | null>(null);

// Close dropdown when clicking outside
onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
};

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};
const { resolvePath } = useNavigationSearch();

// Handle language change with proper redirection
const handleLanguageSelect = async (selectedLanguageId: string) => {
  try {
    // Store current scroll position

    const currentFullPath = route.fullPath.replace(/^\/([a-z]{2})(\/|$)/, "/");
    if (!currentFullPath) {
      const data = await changeLanguage(selectedLanguageId);
      window.location.href = encodeURI(data.redirectUrl as string);
    } else {
      const currentSeoResult = await resolvePath(currentFullPath);
      const currentPathInfo = currentSeoResult?.pathInfo;
      const data = await changeLanguage(selectedLanguageId);
      if (data.redirectUrl) {
        if (currentFullPath.startsWith("/blog/")) {
          window.location.href = encodeURI(
            data.redirectUrl + `/news/` + currentPathInfo
          );
          return;
        }
        if (currentPathInfo) {
          const changedSeoResult = await resolvePath(currentPathInfo);
          const seoPath = changedSeoResult?.seoPathInfo;
          if (seoPath) {
            window.location.href = encodeURI(data.redirectUrl + "/" + seoPath);
          } else {
            window.location.href = encodeURI(
              data.redirectUrl + currentPathInfo
            );
          }
        } else {
          window.location.href = encodeURI(data.redirectUrl + currentFullPath);
        }
      } else {
        window.location.reload();
      }
    }
  } catch (error) {
    console.error("Error changing language:", error);
    // Handle error (show notification, etc.)
  }
};
</script>
<template>
  <div class="relative" ref="dropdownRef">
    <!-- Language Selector Button -->
    <button
      class="flex items-center text-base font-normal text-white bg-transparent border-none"
      id="languageButton"
      @click="toggleDropdown"
      aria-haspopup="true"
      :aria-expanded="isDropdownOpen"
    >
      {{
        currentLanguage
          ? formatLanguageDisplay(currentLanguage)
          : languageIdChain.substring(0, 2).toUpperCase()
      }}
      <svg class="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
          clip-rule="evenodd"
        ></path>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-show="isDropdownOpen"
      class="absolute right-0 mt-2 w-32 bg-light-grey rounded-md shadow-lg overflow-hidden border border-medium-grey z-10"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="languageButton"
    >
      <div class="py-1">
        <a
          href="/en/"
          class="transition duration-300 block px-4 py-2 text-sm text-black hover:bg-white cursor-pointer"
          :class="{ 'bg-gray-50': locale === 'en' }"
          >EN</a
        >
        <a
          href="/fr/"
          class="transition duration-300 block px-4 py-2 text-sm text-black hover:bg-white cursor-pointer"
          :class="{ 'bg-gray-50': locale === 'fr' }"
          >FR</a
        >
        <a
          href="/"
          class="transition duration-300 block px-4 py-2 text-sm text-black hover:bg-white cursor-pointer"
          :class="{ 'bg-gray-50': locale === 'de' }"
          >DE</a
        >
      </div>
    </div>
  </div>
</template>
